import React from "react";
import {
	BrowserRouter as Router,
	Routes,
	Route,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { Toaster } from "react-hot-toast";
import { usePages } from "./hooks/usePage";
import { PageSection } from "@/types/pageBuilder";

// Layouts
import MarketingLayout from "./components/layout/MarketingLayout";
import ParentLayout from "./components/layout/ParentLayout";
import DoulaLayout from "./components/layout/DoulaLayout";
import DoulaOnboardingLayout from "./components/layout/DoulaOnboardingLayout";
import AdminLayout from "./components/layout/AdminLayout";

// Components
import AuthCheck from "./components/AuthCheck";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import ParentSignupPage from "./pages/ParentSignupPage";
import NotFound from "./pages/NotFound";
import OAuthCallback from "./pages/OAuthCallback";

// Parent Pages
import ParentDashboard from "./pages/parent/ParentDashboard";
import SearchDoulas from "./pages/parent/SearchDoulas";
import DoulaProfile from "./pages/parent/DoulaProfile";
import ParentFavorites from "./pages/parent/ParentFavorites";
import ParentMessages from "./pages/parent/ParentMessages";
import ParentBookings from "./pages/parent/ParentBookings";
import ParentFAQ from "./pages/parent/ParentFAQ";

// Doula Pages
import DoulaDashboard from "./components/doula/DoulaDashboard";
import DoulaSignup from "./components/doula/DoulaSignup";
import { DoulaOnboarding } from "./components/doula/DoulaOnboarding";
import DoulaBookings from "./pages/doula/DoulaBookings";
import DoulaMessages from "./pages/doula/DoulaMessages";
import DoulaFAQ from "./pages/doula/DoulaFAQ";
import DoulaEarnings from "./pages/doula/DoulaEarnings";
import DoulaProfileManagement from "./pages/doula/DoulaProfileManagement";

// Admin Pages
import AdminDashboard from "./components/admin/AdminDashboard";
import UserManagement from "./components/admin/UserManagement";
import ApplicationReview from "./components/admin/ApplicationReview";
import DoulaApplicationDetail from "./components/admin/DoulaApplicationDetail";
import AdminMessagingMonitor from "./components/admin/AdminMessagingMonitor";
import FAQManagement from "./components/admin/FAQManagement";
import AdminRoute from "./components/AdminRoute";

// App Layout (handles routing logic)
import AppLayout from "./components/AppLayout";
import Loading from "./components/ui/Loading";

// Allowlist of slugs Strapi is permitted to register as routes.
// Prevents a compromised CMS from shadowing auth or app routes.
const ALLOWED_CMS_SLUGS = new Set(["home", "terms", "privacy"]);

// Map from resolved route path → component. Must stay in sync with ALLOWED_CMS_SLUGS.
// add param to pass the pageBuilder data from Strapi to the page components so they can render dynamic content sections based on the data structure in Strapi, and also add a default component to render if the slug is not found in the map, which can be a simple 404 component that says "Page Not Found" or something similar. This will allow us to easily add new pages and components in Strapi without having to change the code structure of the app, and also allow us to easily handle cases where the slug is not found in the map without breaking the app.

const cmsSlugComponents = (slug: string, pageBuilder?: PageSection[]): React.ReactElement => {
	const component = CMS_SLUG_COMPONENTS[slug];
	if (!component) {
		return (
			<MarketingLayout>
				<Route path="*" element={<NotFound />} />
			</MarketingLayout>
		);
	}
	return <LandingPage pageBuilder={pageBuilder} />;
}

const CMS_SLUG_COMPONENTS: Record<string, React.ReactElement> = {
	"/": <LandingPage />,
	"terms": <LandingPage />,
	"privacy": <LandingPage />,
};

const App: React.FC = () => {
	const { data: pages, isLoading } = usePages();

	return (
		<AuthProvider>
			<SettingsProvider>
				<Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
					<div className="App">
						<Routes>
							{/* CMS-driven marketing routes — only rendered after Strapi resolves.
							    Static auth/app routes below are always available immediately. */}
							{isLoading ? (
								<Route
									path="/"
									element={
										<MarketingLayout>
											<Loading size="full" text="Loading..." />
										</MarketingLayout>
									}
								/>
							) : (
								pages
									?.filter((page) => ALLOWED_CMS_SLUGS.has(page.slug))
									.map((page) => {
										const routePath = page.slug === "home" ? "/" : page.slug;
										return (
											<Route
												key={page.strapiId}
												path={routePath}
												element={
													<MarketingLayout>
														{cmsSlugComponents(routePath, page.pageBuilder)}
													</MarketingLayout>
												}
											/>
										);
									})
							)}

							<Route
								path="/login"
								element={
									<MarketingLayout>
										<LoginPage />
									</MarketingLayout>
								}
							/>

							<Route
								path="/parent-signup"
								element={
									<MarketingLayout hideMarketingNav={true}>
										<AuthCheck>
											<ParentSignupPage />
										</AuthCheck>
									</MarketingLayout>
								}
							/>

							<Route
								path="/doula-signup"
								element={
									<MarketingLayout hideMarketingNav={true}>
										<AuthCheck>
											<DoulaSignup />
										</AuthCheck>
									</MarketingLayout>
								}
							/>

							{/* OAuth Callback Route */}
							<Route path="/oauth/callback" element={<OAuthCallback />} />

							{/* Public Search Route - No authentication required */}
							<Route
								path="/search-doulas"
								element={
									<MarketingLayout>
										<SearchDoulas />
									</MarketingLayout>
								}
							/>

							{/* Public Doula Profile Route - No authentication required */}
							<Route
								path="/doula-profile/:doulaId"
								element={
									<MarketingLayout>
										<DoulaProfile />
									</MarketingLayout>
								}
							/>

							{/* Protected Routes - AppLayout handles auth and routing */}
							<Route path="/*" element={<AppLayout />}>
								{/* Parent Routes */}
								<Route
									path="parent-dashboard"
									element={
										<ParentLayout>
											<ParentDashboard />
										</ParentLayout>
									}
								/>
								<Route
									path="parent-favorites"
									element={
										<ParentLayout>
											<ParentFavorites />
										</ParentLayout>
									}
								/>
								<Route
									path="parent-messages"
									element={
										<ParentLayout>
											<ParentMessages />
										</ParentLayout>
									}
								/>
								<Route
									path="parent-bookings"
									element={
										<ParentLayout>
											<ParentBookings />
										</ParentLayout>
									}
								/>
								<Route
									path="parent-profile"
									element={
										<ParentLayout>
											<div>Parent Profile (Coming Soon)</div>
										</ParentLayout>
									}
								/>
								<Route
									path="parent-faq"
									element={
										<ParentLayout>
											<ParentFAQ />
										</ParentLayout>
									}
								/>

								{/* Doula Routes */}
								<Route
									path="doula-dashboard"
									element={
										<DoulaLayout>
											<DoulaDashboard />
										</DoulaLayout>
									}
								/>
								<Route
									path="doula-onboarding"
									element={
										<DoulaOnboardingLayout>
											<DoulaOnboarding />
										</DoulaOnboardingLayout>
									}
								/>
								<Route
									path="doula-appointments"
									element={
										<DoulaLayout>
											<DoulaBookings />
										</DoulaLayout>
									}
								/>
								<Route
									path="doula-messages"
									element={
										<DoulaLayout>
											<DoulaMessages />
										</DoulaLayout>
									}
								/>
								<Route
									path="doula-faq"
									element={
										<DoulaLayout>
											<DoulaFAQ />
										</DoulaLayout>
									}
								/>
								<Route
									path="doula-earnings"
									element={
										<DoulaLayout>
											<DoulaEarnings />
										</DoulaLayout>
									}
								/>
								<Route
									path="doula-profile-management"
									element={
										<DoulaLayout>
											<DoulaProfileManagement />
										</DoulaLayout>
									}
								/>

								{/* Admin Routes */}
								<Route
									path="admin-dashboard"
									element={
										<AdminRoute>
											<AdminLayout>
												<AdminDashboard />
											</AdminLayout>
										</AdminRoute>
									}
								/>
								<Route
									path="admin-users"
									element={
										<AdminRoute>
											<AdminLayout>
												<UserManagement />
											</AdminLayout>
										</AdminRoute>
									}
								/>
								<Route
									path="admin-applications"
									element={
										<AdminRoute>
											<AdminLayout>
												<ApplicationReview />
											</AdminLayout>
										</AdminRoute>
									}
								/>
								<Route
									path="admin-applications/:id"
									element={
										<AdminRoute>
											<AdminLayout>
												<DoulaApplicationDetail />
											</AdminLayout>
										</AdminRoute>
									}
								/>
								<Route
									path="admin-messaging"
									element={
										<AdminRoute>
											<AdminLayout>
												<AdminMessagingMonitor />
											</AdminLayout>
										</AdminRoute>
									}
								/>
								<Route
									path="admin-faq"
									element={
										<AdminRoute>
											<AdminLayout>
												<FAQManagement />
											</AdminLayout>
										</AdminRoute>
									}
								/>
							</Route>

							{/* 404 Route */}
							<Route path="*" element={<NotFound />} />
						</Routes>

						<Toaster
							position="top-right"
							toastOptions={{
								duration: 4000,
								style: {
									background: "#363636",
									color: "#fff",
								},
							}}
						/>
					</div>
				</Router>
			</SettingsProvider>
		</AuthProvider>
	);
};

export default App;
